import { useEffect, useRef, useState } from "preact/hooks";
import {
  setLedgerAccountChooser,
  setWalletConnectUriSink,
  type LedgerAccount,
  type LedgerAccountRequest,
  type WalletSessionValue,
} from "@tenergy/web-core/wallet";
import { Button, CopyButton } from "./ui.js";

/**
 * The panel's wallet picker.
 *
 * Deliberately plainer than the site's connect modal: this is an internal tool used by a
 * handful of people who already have their wallet installed, so it is a list, a Ledger account
 * step and the WalletConnect URI as text. The site's dialog carries brand copy, install
 * prompts, a QR canvas and phone/desktop branching that none of those people need, and it is
 * not imported here — that copy belongs to `apps/site-tenergy`.
 *
 * Both adapter bridges ARE registered while the dialog is open, because without them a Ledger
 * silently takes its first account and WalletConnect has nowhere to show its pairing URI.
 */
export function WalletPicker({ wallet }: { wallet: WalletSessionValue }) {
  const [ledger, setLedger] = useState<LedgerAccountRequest | null>(null);
  const [relayUri, setRelayUri] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const open = wallet.modalOpen;

  useEffect(() => {
    if (!open) return undefined;
    setLedgerAccountChooser(setLedger);
    setWalletConnectUriSink(setRelayUri);
    return () => {
      setLedgerAccountChooser(null);
      setWalletConnectUriSink(null);
      setLedger(null);
      setRelayUri(null);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    dialogRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") wallet.closeModal();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, wallet]);

  if (!open) return null;

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) wallet.closeModal();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Choose a wallet"
        tabIndex={-1}
        class="w-full max-w-sm rounded-[20px] border border-line bg-surface p-3 shadow-2xl outline-none"
      >
        <h2 class="text-[13px] font-semibold text-ink">Choose a wallet</h2>

        {ledger !== null ? (
          <LedgerAccounts request={ledger} onDone={() => setLedger(null)} />
        ) : (
          <>
            <ul class="mt-2 space-y-1">
              {wallet.wallets.length === 0 ? (
                <li class="py-4 text-center text-[11px] text-ink-muted">
                  {wallet.loadingRuntime ? "Looking for wallets…" : "No wallet was found."}
                </li>
              ) : (
                wallet.wallets.map((entry) => (
                  <li key={entry.id}>
                    <button
                      type="button"
                      disabled={wallet.connecting}
                      onClick={() => {
                        void wallet.connect(entry.id).catch(() => undefined);
                      }}
                      class="flex w-full items-center gap-2 rounded-[12px] border border-line px-2 py-1.5 text-left text-xs text-ink hover:bg-surface-raised disabled:opacity-60"
                    >
                      {entry.icon === null ? null : (
                        <img src={entry.icon} alt="" width="16" height="16" class="rounded" />
                      )}
                      <span class="flex-1">{entry.id}</span>
                      <span class="text-[10px] text-ink-muted">
                        {wallet.connectingWalletId === entry.id ? "connecting…" : entry.readyState}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>

            {relayUri === null ? null : (
              <div class="mt-2 rounded-[12px] border border-line bg-surface-raised p-2">
                <div class="text-[11px] text-ink">WalletConnect pairing URI</div>
                <code class="mt-1 block max-h-20 overflow-auto break-all num text-[10px] text-ink-muted">
                  {relayUri}
                </code>
                <div class="mt-1">
                  <CopyButton value={relayUri} label="copy URI" />
                </div>
              </div>
            )}

            {wallet.error === null ? null : (
              <p role="alert" class="mt-2 text-[11px] text-danger">
                {wallet.error.message}
              </p>
            )}
          </>
        )}

        <div class="mt-3 flex justify-end">
          <Button
            tone="ghost"
            onClick={() => {
              setLedger(null);
              wallet.closeModal();
            }}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function LedgerAccounts({
  request,
  onDone,
}: {
  request: LedgerAccountRequest;
  onDone(): void;
}) {
  const [accounts, setAccounts] = useState<LedgerAccount[]>(request.accounts);
  const [busy, setBusy] = useState(false);

  return (
    <div class="mt-2">
      <p class="text-[11px] text-ink-muted">Pick the account to sign with.</p>
      <ul class="mt-1 space-y-1">
        {accounts.map((account) => (
          <li key={account.path}>
            <button
              type="button"
              onClick={() => {
                request.choose(account);
                onDone();
              }}
              class="flex w-full items-center justify-between gap-2 rounded-[12px] border border-line px-2 py-1.5 text-left num text-[11px] text-ink hover:bg-surface-raised"
            >
              <span>{account.address}</span>
              <span class="text-ink-muted">#{account.index}</span>
            </button>
          </li>
        ))}
      </ul>
      <div class="mt-2 flex gap-2">
        <Button
          tone="ghost"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void request
              .loadMore(accounts.length, accounts.length + 5)
              .then((more) => setAccounts((current) => [...current, ...more]))
              .catch(() => undefined)
              .finally(() => setBusy(false));
          }}
        >
          Load more
        </Button>
        <Button
          tone="ghost"
          onClick={() => {
            request.cancel();
            onDone();
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
